/**
 * Renders the in-app resume preview (same DOM + CSS) into a hidden container,
 * captures it with html2canvas, and returns a PDF blob so the downloaded file
 * matches exactly what the user sees after applying the template.
 *
 * Uses content-aware page breaking: instead of slicing the canvas at a hard
 * pixel offset, it scans ±80 px around each ideal break point and finds the
 * "whitest" (least content) row so section headers and bullet lines are never
 * split across pages.
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import ResumePreviewForPDF from './ResumePreviewForPDF';
import type { ResumeSection } from './utils/parseResumeSections';
import type { SavedProfileDesign } from './utils/savedProfileResumeFile';

const A4_W_MM = 210;
const A4_H_MM = 297;
/** Reserved bottom margin (mm) for consultancy watermark text on every page. */
const FOOTER_WATERMARK_MM = 8;

/**
 * Scan a horizontal band of the canvas around `idealY` and return the Y
 * coordinate of the row that is most "white" (i.e. fewest non-white pixels).
 * This avoids slicing through dark section-header bars or mid-sentence text.
 *
 * @param pixelData  Flat RGBA array from ctx.getImageData(0, 0, w, h)
 * @param canvasW    Canvas width in pixels
 * @param idealY     Where we would ideally break the page
 * @param searchRange  How many pixels above/below idealY to scan
 */
function findSmartBreakY(
  pixelData: Uint8ClampedArray,
  canvasW: number,
  idealY: number,
  searchRange = 80,
): number {
  const from = Math.max(0, idealY - searchRange);
  const to = idealY + searchRange;

  let bestY = idealY;
  let bestScore = -1;

  for (let y = from; y <= to; y++) {
    // Count how many pixels in this row are near-white (r,g,b all > 230)
    let lightCount = 0;
    for (let x = 0; x < canvasW; x++) {
      const i = (y * canvasW + x) * 4;
      if (
        pixelData[i] > 230 &&
        pixelData[i + 1] > 230 &&
        pixelData[i + 2] > 230
      ) {
        lightCount++;
      }
    }
    // Weighted score: prefer rows closer to idealY so pages stay balanced
    const lightness = lightCount / canvasW;
    const proximity = 1 - Math.abs(y - idealY) / (searchRange + 1);
    const score = lightness * 0.75 + proximity * 0.25;

    if (score > bestScore) {
      bestScore = score;
      bestY = y;
    }
  }

  return bestY;
}

export type CaptureResumePreviewPdfOptions = {
  personalInfoLabel?: string;
  /** Matches saved profile / Results step (fonts, accent, caps). */
  design?: SavedProfileDesign;
  /** Printed centered at the bottom of every A4 page (e.g. consultancy name). */
  footerWatermark?: string;
};

/**
 * Generate a PDF that looks exactly like the in-app resume preview.
 * Uses the same React component and CSS as the Results step.
 */
export async function captureResumePreviewAsPdf(
  sections: ResumeSection[],
  templateId: string,
  options: CaptureResumePreviewPdfOptions = {},
): Promise<Blob> {
  const personalInfoLabel = options.personalInfoLabel ?? 'Personal Information';
  const footerWatermark = options.footerWatermark?.trim();

  const [html2canvas, { jsPDF: JsPDF }] = await Promise.all([
    import('html2canvas').then(m => m.default),
    import('jspdf'),
  ]);

  const wrapper = document.createElement('div');
  wrapper.style.cssText =
    'position:fixed;left:-9999px;top:0;width:794px;background:#fff;z-index:-1;overflow:visible;';
  document.body.appendChild(wrapper);

  const root = createRoot(wrapper);
  root.render(
    <ResumePreviewForPDF
      sections={sections}
      templateId={templateId}
      personalInfoLabel={personalInfoLabel}
      design={options.design}
    />,
  );

  try {
    // Allow React to finish rendering (fonts, layout)
    await new Promise<void>(resolve => setTimeout(resolve, 400));
    const el = wrapper.firstElementChild as HTMLElement;
    if (!el) throw new Error('Resume preview did not render');

    const canvas = await html2canvas(el, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    });

    const a4HeightPx = Math.round((A4_H_MM / A4_W_MM) * canvas.width);
    const pdf = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    // Read all pixel data once — cheap to re-access per row
    const ctx2d = canvas.getContext('2d')!;
    const { data: pixelData } = ctx2d.getImageData(0, 0, canvas.width, canvas.height);

    // Build smart page-break Y positions
    const breakYs: number[] = [0]; // start of each page
    let cursor = 0;
    while (cursor < canvas.height) {
      const idealNext = cursor + a4HeightPx;
      if (idealNext >= canvas.height) break; // last (partial) page

      const smartY = findSmartBreakY(pixelData, canvas.width, idealNext);
      breakYs.push(smartY);
      cursor = smartY;
    }
    breakYs.push(canvas.height); // sentinel

    for (let p = 0; p < breakYs.length - 1; p++) {
      if (p > 0) pdf.addPage();

      const srcY = breakYs[p];
      const srcH = breakYs[p + 1] - srcY;

      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = a4HeightPx; // content area height in px (matches contentHeightMm)
      const pCtx = pageCanvas.getContext('2d')!;
      pCtx.fillStyle = '#ffffff';
      pCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      pCtx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);

      const imgData = pageCanvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 0, 0, A4_W_MM, contentHeightMm);

      if (footerWatermark) {
        pdf.setFontSize(8);
        pdf.setTextColor(107, 107, 107);
        pdf.text(footerWatermark, A4_W_MM / 2, A4_H_MM - 3, { align: 'center' });
      }
    }

    return pdf.output('blob');
  } finally {
    root.unmount();
    setTimeout(() => {
      if (wrapper.parentNode) wrapper.remove();
    }, 0);
  }
}
